import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Card } from "../../shared/Card";
import { FormContainer } from "../../shared/FormLayout/FormContainer";
import { FormRow } from "../../shared/FormLayout/FormRow";

import { WorkOrderWithAssignment } from "../../shared/interfaces/WorkOrder";
import { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";

import get from "lodash/get";
import React from "react";
import { CompanyDropdown } from "../../CompanyDropdown";
import { HVSubstationDropdown } from "../../HVSubstationDropdown";
import { SiteDropdown } from "../../SiteDropdown";
import { WorkflowDropdown } from "../../WorkflowDropdown";
import AssetDropdown from "../../data/Assets/AssetDropdown/AssetDropdown";
import { useCompanyAndSiteIds } from "../../data/useCompanyAndSiteIds";
import { Asset } from "../../shared/interfaces/Asset";
import { Attribute } from "../../shared/interfaces/Attribute";
import { WorkflowV2 } from "../../shared/interfaces/WorkflowV2";

interface WorkorderLocationAndWorkflowProps {
  workorder: Partial<WorkOrderWithAssignment>;
  isLoading: boolean;
  editingWorkorder: boolean;
  substationChildAssets?: Asset[];
  selectedSubstationChildAssets?: Asset[];
  onSubstationChange: (substationId?: number, selectedSubstationName?: string, attributes?: Attribute[]) => void;
  onAssetsChange: (assets?: Asset[]) => void;
  onWorkflowChange: (workflow: WorkflowV1 | WorkflowV2) => void;
}

export default function WorkorderLocationAndWorkflow(props: WorkorderLocationAndWorkflowProps) {
  const { t } = useTranslation();
  const { workorder, isLoading, editingWorkorder, onSubstationChange, onAssetsChange, onWorkflowChange } = props;
  const { register, formState, setValue } = useFormContext();
  const [companyName, setCompanyName] = React.useState<string | undefined>(undefined);
  const [siteName, setSiteName] = React.useState<string | undefined>(undefined);

  const { companyId, siteId } = useCompanyAndSiteIds(companyName, siteName, workorder.customerId);

  const assetIdsKey = "assetIds";
  const workflowKey = "workflow";

  function handleCompanyNameChange(companyName: string | undefined) {
    setCompanyName(companyName);
    setSiteName(undefined);
    clearSubstationAndSwitchgear();
  }

  function handleSiteNameChange(siteName: string | undefined) {
    setSiteName(siteName);
    clearSubstationAndSwitchgear();
  }

  function clearSubstationAndSwitchgear() {
    props.onSubstationChange(undefined, undefined, undefined);
    onAssetsChange(undefined);
  }

  function handleAssetsChange(assets: Asset[]) {
    onAssetsChange(assets);
    setValue(
      assetIdsKey,
      assets.map((asset) => asset.id),
    );
    if (assets.length === 0) {
      setValue(workflowKey, undefined, { shouldValidate: true });
    }
  }
  return (
    <Card title={t("workflows.locationAndWorkflow")} className="mb-3" loading={isLoading}>
      {editingWorkorder ? (
        <FormContainer>
          <FormRow
            label={t("common.company")}
            value={
              workorder && (
                <CompanyDropdown
                  customerId={workorder.customerId}
                  selectedCompanyName={companyName}
                  onCompanyChange={handleCompanyNameChange}
                  clearAuto
                />
              )
            }
          />
          <FormRow
            label={t("common.site")}
            value={
              workorder && (
                <SiteDropdown
                  companyId={companyId}
                  customerId={workorder.customerId}
                  selectedSiteName={siteName}
                  onSiteChange={handleSiteNameChange}
                  clearAuto
                />
              )
            }
          />

          <FormRow
            label={t("substations.substation")}
            value={
              workorder && (
                <HVSubstationDropdown
                  key="dropdown-substationId"
                  name={"substationId"}
                  selectedSubstationId={workorder.substationId}
                  companyId={companyId}
                  siteId={siteId}
                  customerId={workorder.customerId}
                  onSubstationChange={onSubstationChange}
                  register={register}
                  formState={formState}
                  setValue={setValue}
                  getAttributes={true}
                />
              )
            }
          />
          <FormRow
            label={t("asset.assets")}
            value={
              workorder && (
                <AssetDropdown
                  multiselect
                  disabled={workorder.substationId == null}
                  key="dropdown-assetId"
                  selectedAssets={props.selectedSubstationChildAssets}
                  substationId={workorder.substationId}
                  onAssetChange={handleAssetsChange}
                  invalidMessage={t("asset.empty", { val: t("asset.asset") })}
                  options={props.substationChildAssets ?? []}
                  width="200px"
                  invalid={get(formState.errors, `assetIds`)}
                  {...register(`assetIds`, {
                    required: true,
                    value: props.selectedSubstationChildAssets?.[0]?.id ?? "",
                  })}
                  disableErrorSpace={true}
                />
              )
            }
          />
          <FormRow
            label={t("workflows.workflow")}
            value={
              workorder && (
                <WorkflowDropdown
                  key="dropdown-workflowId"
                  disabled={props.selectedSubstationChildAssets == null}
                  customerId={workorder.customerId}
                  substationId={workorder.substationId}
                  onWorkflowChange={onWorkflowChange}
                  selectedWorkflowId={workorder.workflowId}
                  selectedAssets={props.selectedSubstationChildAssets}
                  register={register}
                  invalid={get(formState.errors, `workflow`)}
                  formState={formState}
                  setValue={setValue}
                  initialiseFormState
                />
              )
            }
          />
        </FormContainer>
      ) : (
        <FormContainer>
          <FormRow label={t("substations.substation")} value={workorder && workorder.substationName} />
          <FormRow label={t("asset.assets")} value={workorder && workorder.subjectAssetName} />
          <FormRow
            label={t("workorders.address")}
            value={
              <>
                <div>
                  <div>{workorder?.address?.name}</div>
                  <div>{workorder?.address?.line1}</div>
                  <div>{workorder?.address?.line2}</div>
                  <div>{workorder?.address?.line3}</div>
                  <div>{workorder?.address?.line4}</div>
                  <div>{workorder?.address?.locality}</div>
                  <div>{workorder?.address?.region}</div>
                  <div>{workorder?.address?.postCode}</div>
                  <div>{workorder?.address?.country}</div>
                </div>
              </>
            }
          />
          <FormRow label={t("workflows.workflow")} value={workorder && workorder.workflowDescription} />
        </FormContainer>
      )}
    </Card>
  );
}
